import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, openPreviewPdf } from '../api';
import { markdownToHtml } from '../lib/markdown';
import {
  QUESTIONS,
  PHASE_LABEL,
  getQuestion,
  getOption,
  type Phase,
  type QuestionDef,
} from '../lib/v2-questions';

type SessionRow = {
  id: string; version: string; status: string;
  first_name: string | null; email: string | null; phone: string | null;
  industry: string | null; qualifier_choice: string | null; path: string | null;
  demo_mode: boolean; submitted_at: string | null; approved_at: string | null;
  rejected_at: string | null; delivered_at: string | null;
  collected_data: any; metadata: any;
};

type OutputRow = {
  id: string; created_at: string;
  synthesis_text: string | null;
  synthesis_analysis: any | null;
  synthesis_error: string | null;
  synthesis_model_pass1: string | null;
  synthesis_model_pass2: string | null;
  synthesis_tokens_in_pass1: number | null;
  synthesis_tokens_out_pass1: number | null;
  synthesis_tokens_in_pass2: number | null;
  synthesis_tokens_out_pass2: number | null;
  synthesis_generation_ms: number | null;
};

type ResponseRow = Record<string, any> | null;
type Audit = { id: string; created_at: string; event: string; actor: string; metadata: any };

type EngagementContext = { slug: string; name: string } | null;

// Multi-model synthesis runs (Task 035 / amendment-03 Phase B2).
type SynthesisRun = {
  id: string;
  session_id: string;
  run_type: 'primary' | 'alternative' | 'blend';
  is_primary: boolean;
  preset_name: string | null;
  pass_1_provider: string | null;
  pass_1_model: string | null;
  pass_2_provider: string | null;
  pass_2_model: string | null;
  parent_run_ids: string[] | null;
  blend_instructions: string | null;
  status: 'running' | 'completed' | 'failed';
  error_message: string | null;
  synthesis_text: string | null;
  synthesis_analysis: any | null;
  pass_1_input_tokens: number | null;
  pass_1_output_tokens: number | null;
  pass_1_cost_usd: number | null;
  pass_1_duration_ms: number | null;
  pass_2_input_tokens: number | null;
  pass_2_output_tokens: number | null;
  pass_2_cost_usd: number | null;
  pass_2_duration_ms: number | null;
  total_cost_usd: number | null;
  created_at: string;
  completed_at: string | null;
  created_by: string | null;
};

type SessionDetail = {
  session: SessionRow;
  responses: ResponseRow;
  outputs: OutputRow[];
  audit_log: Audit[];
  engagement: EngagementContext;
  synthesis_runs: SynthesisRun[];
};

type TopTab = 'synthesis' | 'analysis' | 'responses';

type GenerateState =
  | { phase: 'idle' }
  | { phase: 'open' }
  | { phase: 'submitting'; preset: string }
  | { phase: 'error'; preset: string; message: string };

type BlendState =
  | { phase: 'idle' }
  | { phase: 'open'; parentIds: [string, string] }
  | { phase: 'submitting'; parentIds: [string, string]; preset: string }
  | { phase: 'error'; parentIds: [string, string]; preset: string; message: string };

const DEFAULT_BLEND_INSTRUCTIONS = 'Critique these and write a fourth that\'s better.';

type Preset =
  | 'anthropic_standard'
  | 'anthropic_fast'
  | 'openai_standard'
  | 'google_standard'
  | 'custom';

const PRESET_LABELS: Record<Preset, string> = {
  anthropic_standard: 'Anthropic standard (Sonnet 4.6 + Opus 4.7)',
  anthropic_fast: 'Anthropic fast (Haiku 4.5 + Sonnet 4.6)',
  openai_standard: 'OpenAI standard (GPT-5 + GPT-5 Pro)',
  google_standard: 'Google standard (Gemini 2.5 Flash + Gemini 2.5 Pro)',
  custom: 'Custom (mix providers)',
};

const PRESET_ESTIMATES: Record<Exclude<Preset, 'custom'>, { cost: string; duration: string }> = {
  anthropic_standard: { cost: '~$0.33', duration: '~85s' },
  anthropic_fast: { cost: '~$0.06', duration: '~45s' },
  openai_standard: { cost: '~$0.55', duration: '~75s' },
  google_standard: { cost: '~$0.05', duration: '~50s' },
};

// Short model badge labels — keep monospace badges narrow in the sidebar.
function shortModel(model: string | null): string {
  if (!model) return '—';
  const m = model.toLowerCase();
  if (m.includes('sonnet')) return 'SONNET';
  if (m.includes('opus')) return 'OPUS';
  if (m.includes('haiku')) return 'HAIKU';
  if (m.includes('gpt-5-pro')) return 'GPT-5-PRO';
  if (m.includes('gpt-5')) return 'GPT-5';
  if (m.includes('gemini-2.5-flash')) return 'GEMINI-FLASH';
  if (m.includes('gemini-2.5-pro')) return 'GEMINI-PRO';
  return model.toUpperCase();
}

function runLabel(run: SynthesisRun, blendIndex: number): string {
  if (run.run_type === 'blend') return `Blend #${blendIndex}`;
  if (run.is_primary) return 'Original';
  // Alternatives — humanize from preset name
  if (run.preset_name === 'anthropic_standard') return 'Anthropic standard';
  if (run.preset_name === 'anthropic_fast') return 'Anthropic fast';
  if (run.preset_name === 'openai_standard') return 'OpenAI standard';
  if (run.preset_name === 'google_standard') return 'Google standard';
  if (run.preset_name === 'custom') {
    return `Custom: ${shortModel(run.pass_1_model)} → ${shortModel(run.pass_2_model)}`;
  }
  return run.preset_name || 'Alternative';
}

function formatCost(usd: number | null): string {
  if (usd == null) return '—';
  return `$${usd.toFixed(2)}`;
}

function formatRunTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export default function Review() {
  const { sessionId } = useParams();
  const [data, setData] = useState<SessionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [topTab, setTopTab] = useState<TopTab>('synthesis');
  const [synthesisTab, setSynthesisTab] = useState<'edit' | 'preview'>('edit');
  const [editorText, setEditorText] = useState('');
  const [savedText, setSavedText] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [busy, setBusy] = useState<null | 'approve' | 'reject' | 'regenerate' | 'preview'>(null);
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  // Multi-model synthesis (Task 035) — versions sidebar + compare mode
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [generateState, setGenerateState] = useState<GenerateState>({ phase: 'idle' });
  const [promotePromptId, setPromotePromptId] = useState<string | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [comparePeerId, setComparePeerId] = useState<string | null>(null);
  // Blend selection — B3 enables the action; modal opens when 2 selected
  const [blendSelection, setBlendSelection] = useState<Set<string>>(new Set());
  const [blendState, setBlendState] = useState<BlendState>({ phase: 'idle' });

  async function reload() {
    if (!sessionId) return;
    setError(null);
    try {
      const result = await api.session(sessionId) as SessionDetail;
      setData(result);
      // Default selection to current primary run; fall back to legacy
      // tempo_outputs.synthesis_text only if synthesis_runs hasn't backfilled.
      const runs = result.synthesis_runs || [];
      const primary = runs.find((r: SynthesisRun) => r.is_primary && r.status === 'completed');
      const initialId = selectedRunId && runs.some((r: SynthesisRun) => r.id === selectedRunId)
        ? selectedRunId
        : (primary?.id ?? runs.find((r: SynthesisRun) => r.status === 'completed')?.id ?? null);
      setSelectedRunId(initialId);
      const text = (initialId
        ? runs.find((r: SynthesisRun) => r.id === initialId)?.synthesis_text
        : null) || result.outputs?.[0]?.synthesis_text || '';
      setEditorText(text);
      setSavedText(text);
    } catch (err: any) {
      setError(err?.message || 'Could not load session.');
    }
  }

  useEffect(() => { reload(); }, [sessionId]);

  const dirty = editorText !== savedText;
  // Hooks before early returns to keep hook count stable across renders
  // (React #310 if useMemo is after the !data return).
  const previewHtml = useMemo(() => markdownToHtml(editorText), [editorText]);

  async function handleSave() {
    if (!sessionId || !dirty || saving) return;
    setSaving(true);
    try {
      await api.patchSynthesis(sessionId, editorText);
      setSavedText(editorText);
      setSavedAt(new Date());
    } catch (err: any) {
      setError(err?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    if (!sessionId) return;
    if (dirty) {
      setError('Save your edits before approving.');
      return;
    }
    setBusy('approve');
    setShowApprove(false);
    try {
      const result = await api.approveSession(sessionId);
      if (result.status === 'delivered') {
        await reload();
      } else {
        await reload();
        setError(result.error || 'Approved, but delivery failed.');
      }
    } catch (err: any) {
      setError(err?.message || 'Approve failed.');
    } finally {
      setBusy(null);
    }
  }

  async function handleReject() {
    if (!sessionId) return;
    setBusy('reject');
    setShowReject(false);
    try {
      await api.rejectSession(sessionId, rejectNote.trim() || undefined);
      setRejectNote('');
      await reload();
    } catch (err: any) {
      setError(err?.message || 'Reject failed.');
    } finally {
      setBusy(null);
    }
  }

  async function handleRegenerate() {
    if (!sessionId) return;
    if (!confirm('Re-run synthesis? Your current edits will be lost. Synthesis takes ~90 seconds.')) return;
    setBusy('regenerate');
    try {
      await api.regenerateSession(sessionId);
      await reload();
    } catch (err: any) {
      setError(err?.message || 'Regenerate failed.');
    } finally {
      setBusy(null);
    }
  }

  async function handlePreviewPdf() {
    if (!sessionId) return;
    setBusy('preview');
    setError(null);
    try {
      await openPreviewPdf(sessionId, editorText);
    } catch (err: any) {
      setError(err?.message || 'Preview PDF failed.');
    } finally {
      setBusy(null);
    }
  }

  // Multi-model synthesis handlers (Task 035) ----------------------------

  function handleSelectRun(runId: string) {
    if (!data) return;
    const run = data.synthesis_runs.find(r => r.id === runId);
    if (!run || run.status !== 'completed') return;
    setSelectedRunId(runId);
    const text = run.synthesis_text || '';
    setEditorText(text);
    setSavedText(text);
    if (compareMode && comparePeerId === runId) {
      // Avoid same-run on both sides; reset peer
      const peer = data.synthesis_runs.find(r => r.id !== runId && r.status === 'completed');
      setComparePeerId(peer?.id ?? null);
    }
  }

  async function handleGenerateAlternative(args: {
    preset: Preset;
    pass1?: { provider: string; model: string };
    pass2?: { provider: string; model: string };
  }) {
    if (!sessionId) return;
    setGenerateState({ phase: 'submitting', preset: args.preset });
    try {
      const body: any = { preset: args.preset };
      if (args.preset === 'custom') {
        body.pass_1 = args.pass1;
        body.pass_2 = args.pass2;
      }
      const result = await api.generateSynthesisRun(sessionId, body);
      const run: SynthesisRun = result.run;
      if (run.status === 'failed') {
        setGenerateState({
          phase: 'error',
          preset: args.preset,
          message: run.error_message || 'Generation failed.',
        });
      } else {
        setGenerateState({ phase: 'idle' });
      }
      await reload();
    } catch (err: any) {
      setGenerateState({
        phase: 'error',
        preset: args.preset,
        message: err?.message || 'Network error during generation.',
      });
    }
  }

  async function handlePromoteRun(runId: string) {
    if (!sessionId) return;
    try {
      await api.promoteSynthesisRun(sessionId, runId);
      setPromotePromptId(null);
      // Adopt the new primary as selected so the editor reflects it
      setSelectedRunId(runId);
      await reload();
    } catch (err: any) {
      setError(err?.message || 'Promote failed.');
    }
  }

  function toggleBlendSelection(runId: string) {
    setBlendSelection(prev => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId); else next.add(runId);
      return next;
    });
  }

  function openBlendModal() {
    if (blendSelection.size !== 2) return;
    const [a, b] = Array.from(blendSelection);
    setBlendState({ phase: 'open', parentIds: [a, b] });
  }

  async function handleBlendSubmit(args: {
    parentIds: [string, string];
    preset: Preset;
    blendInstructions: string;
    pass2?: { provider: string; model: string };
  }) {
    if (!sessionId) return;
    setBlendState({ phase: 'submitting', parentIds: args.parentIds, preset: args.preset });
    try {
      const body: any = {
        parent_run_ids: args.parentIds,
        preset: args.preset,
        blend_instructions: args.blendInstructions,
      };
      if (args.preset === 'custom') body.pass_2 = args.pass2;
      const result = await api.blendSynthesisRuns(sessionId, body);
      const run: SynthesisRun = result.run;
      if (run.status === 'failed') {
        setBlendState({
          phase: 'error',
          parentIds: args.parentIds,
          preset: args.preset,
          message: run.error_message || 'Blend failed.',
        });
      } else {
        setBlendState({ phase: 'idle' });
        setBlendSelection(new Set());
      }
      await reload();
    } catch (err: any) {
      setBlendState({
        phase: 'error',
        parentIds: args.parentIds,
        preset: args.preset,
        message: err?.message || 'Network error during blend.',
      });
    }
  }

  if (error && !data) {
    return <p className="font-serif text-accent">{error}</p>;
  }
  if (!data) {
    return <p className="font-mono text-[11px] uppercase tracking-wider text-ink-light">Loading…</p>;
  }

  const session = data.session;
  const output = data.outputs[0] || null;
  const isV1 = session.version === 'v1';
  const operatorName = (isV1 ? session.collected_data?.first_name : session.first_name) || '(no name)';
  const operatorEmail = (isV1 ? session.collected_data?.email : session.email) || '(no email)';
  const operatorPhone = (isV1 ? session.collected_data?.phone : session.phone) || null;

  // Run-derived values for sidebar + compare mode (Task 035)
  const runs = data.synthesis_runs || [];
  const sortedRuns: SynthesisRun[] = (() => {
    const primary = runs.find(r => r.is_primary);
    const others = runs.filter(r => !r.is_primary).sort((a, b) => a.created_at.localeCompare(b.created_at));
    return primary ? [primary, ...others] : others;
  })();
  const selectedRun = sortedRuns.find(r => r.id === selectedRunId) || sortedRuns[0] || null;
  const primaryRun = sortedRuns.find(r => r.is_primary) || null;
  const isViewingPrimary = !!selectedRun?.is_primary;
  const sessionTotalCost = runs.reduce((sum, r) => sum + (r.total_cost_usd || 0), 0);
  const primaryCount = runs.filter(r => r.is_primary).length;
  const altCount = runs.filter(r => r.run_type === 'alternative' && !r.is_primary).length;
  const blendCount = runs.filter(r => r.run_type === 'blend').length;
  // Compare peer defaults: most-recently-created non-selected completed run
  const peerRun = (() => {
    if (!compareMode) return null;
    if (comparePeerId) return sortedRuns.find(r => r.id === comparePeerId) || null;
    const candidates = sortedRuns.filter(r => r.id !== selectedRunId && r.status === 'completed');
    return candidates[candidates.length - 1] || null;
  })();
  // Editing only allowed on primary (alternatives are read-only inspection)
  const editingDisabled = !isViewingPrimary;

  return (
    <div className="max-w-[1400px] -mx-4 md:-mx-10 -my-8 md:-my-12 pb-24">
      {data.engagement && <EngagementSubnav engagement={data.engagement} sessionId={sessionId!} />}
      <div className="border-b border-rule px-4 md:px-10 py-4 bg-paper sticky top-0 z-10">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <Link to="/queue" className="font-mono text-[11px] uppercase tracking-wider text-ink-light hover:text-accent">
              ← Back to queue
            </Link>
            <h1 className="font-display text-2xl font-medium text-ink mt-1">
              {operatorName}{' '}
              <span className="font-serif italic text-ink-light text-base">
                · {industryShort(session.industry)} · {session.path === 'pathA' ? 'Founder-Led' : session.path === 'pathB' ? 'Management-Led' : '(no path)'}
              </span>
            </h1>
          </div>
          <StatusPill status={session.status} demo={session.demo_mode} />
        </div>
      </div>

      {error && (
        <div className="mx-4 md:mx-10 mt-4 px-4 py-3 bg-paper border border-accent text-accent font-serif text-sm">
          {error}{' '}
          <button onClick={() => setError(null)} className="font-mono text-[11px] uppercase tracking-wider ml-2 hover:underline">dismiss</button>
        </div>
      )}

      <div className="grid md:grid-cols-[240px_1fr_280px] gap-0 md:gap-6 px-4 md:px-10 mt-6">
        {/* Left rail — operator metadata */}
        <aside className="space-y-4 md:sticky md:top-24 self-start">
          <Section title="Operator">
            <div className="font-serif text-base text-ink">{operatorName}</div>
            <div className="font-mono text-xs text-ink-light break-all mt-1">{operatorEmail}</div>
            {operatorPhone && <div className="font-mono text-xs text-ink-light mt-1">{operatorPhone}</div>}
          </Section>
          <Section title="Operating reality">
            <div className="font-serif text-sm text-ink">
              <span className="font-mono text-xs text-ink-light mr-2">{session.qualifier_choice || '—'}</span>
              {qualifierLabel(session.qualifier_choice)}
            </div>
          </Section>
          <Section title="Industry">
            <div className="font-serif text-sm text-ink">{industryShort(session.industry)}</div>
          </Section>
          {session.demo_mode && (
            <div className="bg-cream-dark border-l-2 border-gold p-3 font-serif text-sm text-ink-soft italic">
              Demo session — auto-delivers without admin gate.
            </div>
          )}
          <Section title="Submitted">
            <div className="font-mono text-xs text-ink-light">{session.submitted_at ? new Date(session.submitted_at).toLocaleString() : '—'}</div>
          </Section>
          {(output || primaryRun) && (
            <Section title="Synthesis instrumentation" collapsible>
              <div className="font-mono text-xs text-ink-light space-y-1">
                <div>Pass 1: {primaryRun?.pass_1_model || output?.synthesis_model_pass1 || '—'} · {(primaryRun?.pass_1_input_tokens ?? output?.synthesis_tokens_in_pass1 ?? 0)+'/'+(primaryRun?.pass_1_output_tokens ?? output?.synthesis_tokens_out_pass1 ?? 0)} tokens</div>
                <div>Pass 2: {primaryRun?.pass_2_model || output?.synthesis_model_pass2 || '—'} · {(primaryRun?.pass_2_input_tokens ?? output?.synthesis_tokens_in_pass2 ?? 0)+'/'+(primaryRun?.pass_2_output_tokens ?? output?.synthesis_tokens_out_pass2 ?? 0)} tokens</div>
                <div>Total: {(() => {
                  const ms = (primaryRun?.pass_1_duration_ms ?? 0) + (primaryRun?.pass_2_duration_ms ?? 0);
                  if (ms > 0) return (ms / 1000).toFixed(1) + 's';
                  return output?.synthesis_generation_ms != null ? (output.synthesis_generation_ms / 1000).toFixed(1) + 's' : '—';
                })()}</div>
                {sessionTotalCost > 0 && (
                  <>
                    <div className="border-t border-rule my-2" />
                    <div>Synthesis spend: <span className="text-ink">${sessionTotalCost.toFixed(2)}</span></div>
                    <div>{runs.length} run{runs.length === 1 ? '' : 's'} ({primaryCount} primary, {altCount} alt{altCount === 1 ? '' : 's'}{blendCount ? `, ${blendCount} blend${blendCount === 1 ? '' : 's'}` : ''})</div>
                  </>
                )}
              </div>
            </Section>
          )}
        </aside>

        {/* Center pane — top-level tabs (full width now) */}
        <div>
          <div className="flex items-center gap-1 border-b border-rule mb-5 justify-between">
            <div className="flex items-center gap-1">
              <TopTabButton active={topTab === 'synthesis'} onClick={() => setTopTab('synthesis')}>Synthesis</TopTabButton>
              <TopTabButton active={topTab === 'analysis'} onClick={() => setTopTab('analysis')}>Internal Analysis</TopTabButton>
              <TopTabButton active={topTab === 'responses'} onClick={() => setTopTab('responses')}>Operator Responses</TopTabButton>
            </div>
            {/* Compare mode toggle sits to the right of the tab list per
                amendment-03 spec. Hidden on Operator Responses (responses
                are identical across model versions; nothing to compare). */}
            {topTab !== 'responses' && (
              <label className="font-mono text-[11px] uppercase tracking-wider text-ink-light flex items-center gap-1.5 cursor-pointer pr-2">
                <input
                  type="checkbox"
                  checked={compareMode}
                  onChange={(e) => {
                    setCompareMode(e.target.checked);
                    if (e.target.checked && !comparePeerId) {
                      const peer = sortedRuns.find(r => r.id !== selectedRunId && r.status === 'completed');
                      setComparePeerId(peer?.id ?? null);
                    }
                  }}
                  className="accent-gold"
                />
                Compare
              </label>
            )}
          </div>

          {topTab === 'synthesis' && (
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="font-mono text-[11px] uppercase tracking-wider text-ink-light">
                  Synthesis text · <span className="text-ink">{selectedRun ? runLabel(selectedRun, 1) : 'No run'}</span>
                  {selectedRun?.is_primary && <span className="ml-2 text-gold-warm">★ PRIMARY</span>}
                  {!isViewingPrimary && <span className="ml-2 italic text-ink-light">(read-only)</span>}
                </div>
                {!compareMode && (
                  <div className="flex gap-1">
                    <SubTabButton active={synthesisTab === 'edit'} onClick={() => setSynthesisTab('edit')}>Edit</SubTabButton>
                    <SubTabButton active={synthesisTab === 'preview'} onClick={() => setSynthesisTab('preview')}>Preview</SubTabButton>
                  </div>
                )}
              </div>

              {!output && (
                <div className="bg-paper border border-rule p-4 font-serif text-ink-light italic">
                  No synthesis yet. The cron dispatcher should pick this session up within a minute of submission.
                </div>
              )}
              {output?.synthesis_error && (
                <div className="bg-paper border border-accent p-4 font-serif text-sm text-ink mb-3">
                  <strong className="text-accent">Synthesis failed.</strong> <span className="text-ink-soft">{output.synthesis_error}</span>
                  <div className="mt-2">
                    <button
                      onClick={handleRegenerate}
                      disabled={busy !== null}
                      className="font-mono text-[11px] uppercase tracking-wider text-accent hover:underline disabled:opacity-50"
                    >
                      Re-run synthesis →
                    </button>
                  </div>
                </div>
              )}

              {!compareMode && synthesisTab === 'edit' && (
                <textarea
                  value={editorText}
                  onChange={(e) => setEditorText(e.target.value)}
                  spellCheck={true}
                  readOnly={editingDisabled}
                  className={`w-full min-h-[600px] p-5 bg-paper border border-rule font-serif text-base text-ink leading-relaxed focus:outline-none focus:border-accent resize-y ${editingDisabled ? 'opacity-80 cursor-not-allowed' : ''}`}
                  placeholder="Synthesis markdown — six sections per the spec."
                />
              )}
              {!compareMode && synthesisTab === 'preview' && (
                <article
                  className="prose prose-stone bg-paper border border-rule p-6 max-w-none text-ink"
                  style={{ fontFamily: "'Newsreader', Georgia, serif", fontSize: '16px', lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              )}
              {compareMode && (
                <CompareSynthesis
                  left={selectedRun}
                  right={peerRun}
                  allRuns={sortedRuns}
                  onSelectPeer={(id) => setComparePeerId(id)}
                />
              )}

              {!compareMode && (
                <div className="flex items-center justify-between mt-3 text-xs">
                  <div className="font-mono text-[11px] uppercase tracking-wider text-ink-light">
                    {editingDisabled ? '' : dirty ? 'Unsaved changes' : savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : 'Up to date'}
                  </div>
                  <div className="flex gap-3">
                    {isViewingPrimary && (
                      <button
                        onClick={handleRegenerate}
                        disabled={busy !== null}
                        className="font-mono text-[11px] uppercase tracking-wider text-ink-light hover:text-accent disabled:opacity-50"
                      >
                        {busy === 'regenerate' ? 'Re-running…' : 'Re-generate synthesis'}
                      </button>
                    )}
                    <button
                      onClick={() => navigator.clipboard?.writeText(editorText)}
                      className="font-mono text-[11px] uppercase tracking-wider text-ink-light hover:text-accent"
                    >
                      Copy markdown
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {topTab === 'analysis' && !compareMode && (
            (selectedRun?.synthesis_analysis || output?.synthesis_analysis)
              ? <AnalysisFull analysis={selectedRun?.synthesis_analysis || output?.synthesis_analysis} />
              : <div className="bg-paper border border-rule p-6 font-serif text-ink-light italic">No Pass 1 analysis available yet.</div>
          )}
          {topTab === 'analysis' && compareMode && (
            <CompareAnalysis
              left={selectedRun}
              right={peerRun}
              allRuns={sortedRuns}
              onSelectPeer={(id) => setComparePeerId(id)}
            />
          )}

          {topTab === 'responses' && (
            <ResponsesFull
              responses={data.responses}
              session={session}
            />
          )}
        </div>

        {/* Right rail — versions sidebar (Task 035) */}
        <aside className="md:sticky md:top-24 self-start">
          <VersionsSidebar
            runs={sortedRuns}
            selectedRunId={selectedRunId}
            onSelect={handleSelectRun}
            onPromote={(id) => setPromotePromptId(id)}
            promotePromptId={promotePromptId}
            onPromoteCancel={() => setPromotePromptId(null)}
            onPromoteConfirm={handlePromoteRun}
            onGenerateClick={() => setGenerateState({ phase: 'open' })}
            onBlendClick={openBlendModal}
            blendSelection={blendSelection}
            onToggleBlend={toggleBlendSelection}
          />
        </aside>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-rule bg-paper py-3 px-4 md:pl-72 md:pr-10 z-20">
        <div className="flex items-center justify-between gap-3 max-w-[1400px] mx-auto">
          <Link to="/queue" className="font-mono text-[11px] uppercase tracking-wider text-ink-light hover:text-accent">
            ← Back to queue
          </Link>
          <div className="flex gap-3">
            {session.status === 'submitted' && (
              <>
                <button
                  onClick={() => setShowReject(true)}
                  disabled={busy !== null}
                  className="px-4 py-2 border border-accent text-accent font-serif text-sm hover:bg-accent hover:text-paper disabled:opacity-50 transition-colors"
                >
                  Reject
                </button>
                <button
                  onClick={handleSave}
                  disabled={!dirty || saving}
                  className="px-4 py-2 border border-rule text-ink-soft font-serif text-sm hover:border-ink disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving…' : 'Save draft'}
                </button>
                <button
                  onClick={handlePreviewPdf}
                  disabled={!output || !!output.synthesis_error || busy !== null}
                  className="px-4 py-2 border border-rule text-ink-soft font-serif text-sm hover:border-accent hover:text-accent disabled:opacity-50 transition-colors"
                  title="Render the current draft as a PDF in a new tab. No save, no email."
                >
                  {busy === 'preview' ? 'Rendering…' : 'Preview PDF'}
                </button>
                <button
                  onClick={() => setShowApprove(true)}
                  disabled={!output || !!output.synthesis_error || dirty || busy !== null}
                  className="px-5 py-2 bg-ink text-paper font-serif text-sm hover:bg-accent disabled:opacity-50 transition-colors"
                  title={dirty ? 'Save your edits first.' : ''}
                >
                  {busy === 'approve' ? 'Sending…' : 'Approve →'}
                </button>
              </>
            )}
            {session.status !== 'submitted' && (
              <span className="font-mono text-[11px] uppercase tracking-wider text-ink-light px-4 py-2">
                {session.status === 'delivered' ? 'Delivered — read-only' :
                 session.status === 'rejected' ? 'Rejected — read-only' :
                 session.status === 'approved' ? 'Approved (delivery pending)' :
                 'Read-only'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Approve modal */}
      {showApprove && output && (
        <Modal onClose={() => setShowApprove(false)}>
          <h2 className="font-display text-2xl font-medium text-ink mb-3">Send synthesis to {operatorName}?</h2>
          <div className="space-y-2 text-sm font-serif text-ink-soft mb-5">
            <div><span className="font-mono text-[11px] uppercase tracking-wider text-ink-light w-20 inline-block">To</span> {operatorEmail}</div>
            <div><span className="font-mono text-[11px] uppercase tracking-wider text-ink-light w-20 inline-block">Subject</span> {operatorName}, your Cadence Advisers diagnostic</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light mt-3">Preview</div>
            <div className="text-sm text-ink bg-cream p-3 border border-rule italic line-clamp-4">
              {(editorText || '').slice(0, 240)}{(editorText || '').length > 240 ? '…' : ''}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light mt-2">
              The synthesis ships as a PDF attachment plus inline HTML in the body.
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowApprove(false)} className="px-4 py-2 font-serif text-sm text-ink-light hover:text-ink">Back to edit</button>
            <button onClick={handleApprove} className="px-5 py-2 bg-ink text-paper font-serif text-sm hover:bg-accent">Send</button>
          </div>
        </Modal>
      )}

      {/* Generate alternative modal (Task 035) */}
      {generateState.phase !== 'idle' && (
        <GenerateAlternativeModal
          state={generateState}
          onCancel={() => setGenerateState({ phase: 'idle' })}
          onSubmit={handleGenerateAlternative}
        />
      )}

      {/* Blend modal (Task 036) */}
      {blendState.phase !== 'idle' && (
        <BlendModal
          state={blendState}
          runs={sortedRuns}
          onCancel={() => setBlendState({ phase: 'idle' })}
          onSubmit={handleBlendSubmit}
        />
      )}

      {/* Reject modal */}
      {showReject && (
        <Modal onClose={() => setShowReject(false)}>
          <h2 className="font-display text-2xl font-medium text-ink mb-3">Reject this submission?</h2>
          <p className="font-serif text-sm text-ink-soft mb-4">
            Marks the submission as out-of-fit. <strong>No automatic email is sent</strong> — you'll handle the conversation personally.
          </p>
          <label className="block mb-5">
            <span className="font-mono text-[11px] uppercase tracking-wider text-ink-light mb-1 block">Internal note (optional, audit log only)</span>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={3}
              className="w-full p-2 border border-rule bg-paper font-serif text-sm focus:outline-none focus:border-accent"
              placeholder="Why this submission was out-of-fit…"
            />
          </label>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowReject(false)} className="px-4 py-2 font-serif text-sm text-ink-light hover:text-ink">Cancel</button>
            <button onClick={handleReject} className="px-5 py-2 border border-accent text-accent font-serif text-sm hover:bg-accent hover:text-paper">Reject</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function Section({ title, children, collapsible = false }: { title: string; children: React.ReactNode; collapsible?: boolean }) {
  const [open, setOpen] = useState(!collapsible);
  return (
    <div className="bg-paper border border-rule p-3">
      <button
        onClick={() => collapsible && setOpen(v => !v)}
        className={`flex items-center justify-between w-full font-mono text-[10px] uppercase tracking-widest text-ink-light ${collapsible ? 'hover:text-ink' : 'cursor-default'}`}
      >
        <span>{title}</span>
        {collapsible && <span>{open ? '−' : '+'}</span>}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

function TopTabButton({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider border-b-2 -mb-px transition-colors ${
        active
          ? 'border-gold text-ink'
          : 'border-transparent text-ink-light hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

function SubTabButton({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider border-b-2 ${
        active ? 'border-ink text-ink' : 'border-transparent text-ink-light hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

function StatusPill({ status, demo }: { status: string; demo: boolean }) {
  if (demo) return <span className="font-mono text-[10px] uppercase tracking-widest text-gold-warm bg-cream-dark px-2 py-1">demo</span>;
  const map: Record<string, { color: string; bg: string }> = {
    submitted: { color: '#1e40af', bg: '#dbeafe' },
    in_progress: { color: '#6b7280', bg: '#f3f4f6' },
    approved: { color: '#1e40af', bg: '#dbeafe' },
    delivered: { color: '#065f46', bg: '#d1fae5' },
    rejected: { color: '#991b1b', bg: '#fee2e2' },
  };
  const s = map[status] || { color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1" style={{ background: s.bg, color: s.color }}>
      {status}
    </span>
  );
}

// ─── Internal Analysis tab ─────────────────────────────────────────

function AnalysisFull({ analysis }: { analysis: any }) {
  const score = analysis.maturity_score;
  const register = analysis.register;
  const rationale = analysis.register_rationale;
  const patterns: string[] = Array.isArray(analysis.dominant_patterns) ? analysis.dominant_patterns : [];
  const antiRecs: string[] = Array.isArray(analysis.anti_recommendations) ? analysis.anti_recommendations : [];
  const friction: any[] = Array.isArray(analysis.top_friction_points) ? analysis.top_friction_points : [];
  const priorities: any[] = Array.isArray(analysis.ranked_priorities) ? analysis.ranked_priorities : [];
  const tone = analysis.tone_flags || null;
  const next = analysis.next_step || null;
  const industryNotes = analysis.industry_specific_notes || null;

  return (
    <div className="bg-paper border border-rule">
      {/* Score + register header */}
      <div className="border-b border-rule p-6">
        <div className="flex items-baseline gap-4 mb-3">
          <div className="font-display text-5xl font-medium text-ink">{score ?? '—'}</div>
          <div className="font-mono text-xs uppercase tracking-widest text-ink-light">/&nbsp;10</div>
          <div className="font-serif text-base text-gold-warm pl-3 border-l border-rule">{register || '—'}</div>
        </div>
        {rationale && (
          <p className="font-serif text-sm italic text-ink-light leading-relaxed max-w-3xl">{rationale}</p>
        )}
      </div>

      {/* Patterns */}
      {patterns.length > 0 && (
        <AnalysisSection title="Patterns">
          <ul className="space-y-3">
            {patterns.map((p, i) => (
              <li key={i} className="font-serif text-sm text-ink leading-relaxed pl-4 border-l-2 border-rule">{p}</li>
            ))}
          </ul>
        </AnalysisSection>
      )}

      {/* Anti-recommendations — accent-bordered, impossible to miss */}
      {antiRecs.length > 0 && (
        <AnalysisSection title="Recommendations to AVOID" emphasis>
          <ul className="space-y-3">
            {antiRecs.map((r, i) => (
              <li key={i} className="font-serif text-sm text-ink leading-relaxed pl-4 border-l-2 border-accent bg-cream-dark/40 p-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-accent mr-2">avoid</span>
                {r}
              </li>
            ))}
          </ul>
        </AnalysisSection>
      )}

      {/* Tone flags — operator psychology read */}
      {tone && Object.keys(tone).length > 0 && (
        <AnalysisSection title="Tone flags">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
            {Object.entries(tone).map(([k, v]) => (
              <div key={k}>
                <dt className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">{k.replace(/_/g, ' ')}</dt>
                <dd className="font-serif text-sm text-ink">{formatToneValue(v)}</dd>
              </div>
            ))}
          </dl>
        </AnalysisSection>
      )}

      {/* Key signals — top friction points with question evidence */}
      {friction.length > 0 && (
        <AnalysisSection title="Key signals">
          <p className="font-serif text-xs italic text-ink-light mb-4">
            Each entry names the operational area, the questions it surfaces from, and why it matters.
          </p>
          <div className="space-y-5">
            {friction.map((f, i) => (
              <div key={i} className="border-l-2 border-gold pl-4">
                <div className="flex items-baseline justify-between gap-4 flex-wrap mb-1">
                  <h4 className="font-serif text-base font-medium text-ink">{f.area || '(unnamed)'}</h4>
                  {f.fixable_in && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light">
                      fixable in {f.fixable_in}
                    </span>
                  )}
                </div>
                {f.evidence && (
                  <p className="font-mono text-[11px] text-gold-warm mt-1 mb-2 leading-relaxed">
                    {f.evidence}
                  </p>
                )}
                {f.cost && (
                  <p className="font-serif text-sm text-ink-soft leading-relaxed">{f.cost}</p>
                )}
              </div>
            ))}
          </div>
        </AnalysisSection>
      )}

      {/* Ranked priorities */}
      {priorities.length > 0 && (
        <AnalysisSection title="Ranked priorities">
          <ol className="space-y-4">
            {priorities.map((p, i) => (
              <li key={i} className="bg-cream-dark/60 p-4 border border-rule">
                <div className="flex items-baseline gap-3 flex-wrap mb-2">
                  <span className="font-display text-xl font-medium text-ink">{i + 1}</span>
                  {p.leverage && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-accent">
                      {p.leverage} leverage
                    </span>
                  )}
                </div>
                <h4 className="font-serif text-base text-ink leading-snug mb-2">{p.priority || '(no priority)'}</h4>
                {p.why_now && (
                  <div className="mb-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mr-1">why now ·</span>
                    <span className="font-serif text-sm text-ink-soft">{p.why_now}</span>
                  </div>
                )}
                {p.depends_on && (
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mr-1">depends on ·</span>
                    <span className="font-serif text-sm text-ink-soft italic">{p.depends_on}</span>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </AnalysisSection>
      )}

      {/* Next-step suggestion (Pass 1's recommendation; the Pass 2 writer
          may diverge from this — useful to compare) */}
      {next && (
        <AnalysisSection title="Pass 1 next-step suggestion">
          {next.format && (
            <span className="inline-block font-mono text-[10px] uppercase tracking-widest text-gold-warm bg-cream-dark px-2 py-1 mb-3">
              format · {next.format}
            </span>
          )}
          {next.action && (
            <p className="font-serif text-base text-ink leading-relaxed mb-3">{next.action}</p>
          )}
          {next.rationale && (
            <p className="font-serif text-sm italic text-ink-light leading-relaxed">{next.rationale}</p>
          )}
        </AnalysisSection>
      )}

      {/* Industry notes */}
      {industryNotes && (
        <AnalysisSection title="Industry-specific notes">
          <p className="font-serif text-sm text-ink leading-relaxed">{industryNotes}</p>
        </AnalysisSection>
      )}
    </div>
  );
}

function AnalysisSection({ title, children, emphasis = false }: { title: string; children: React.ReactNode; emphasis?: boolean }) {
  return (
    <section className="border-b border-rule last:border-b-0 p-6">
      <h3 className={`font-mono text-[11px] uppercase tracking-widest mb-4 ${emphasis ? 'text-accent' : 'text-gold-warm'}`}>
        {title}
      </h3>
      {children}
    </section>
  );
}

function formatToneValue(v: any): string {
  if (v === true) return 'true';
  if (v === false) return 'false';
  if (v == null) return '—';
  return String(v);
}

// ─── Operator Responses tab ────────────────────────────────────────

function ResponsesFull({ responses, session }: { responses: ResponseRow; session: SessionRow }) {
  if (!responses) {
    return (
      <div className="bg-paper border border-rule p-6 font-serif text-ink-light italic">
        No responses recorded for this session.
      </div>
    );
  }

  // Group questions by phase, filtered by which path the operator took.
  const isPathA = session.path === 'pathA';
  const isPathB = session.path === 'pathB';

  // Phase render order — mirrors the operator's flow.
  const sections: Array<{ phase: Phase; questions: QuestionDef[] }> = [
    { phase: 'qualifier', questions: QUESTIONS.filter(q => q.phase === 'qualifier') },
    { phase: 'context', questions: QUESTIONS.filter(q => q.phase === 'context') },
  ];
  if (isPathA) {
    sections.push({ phase: 'pathA', questions: QUESTIONS.filter(q => q.phase === 'pathA') });
  } else if (isPathB) {
    sections.push({ phase: 'pathB', questions: QUESTIONS.filter(q => q.phase === 'pathB') });
  }
  sections.push({ phase: 'convergence', questions: QUESTIONS.filter(q => q.phase === 'convergence') });

  return (
    <div className="bg-paper border border-rule">
      {/* Intake header — synthetic phase from session metadata */}
      <ResponseSectionHeader phase="intake" />
      <div className="px-6 pb-6 space-y-4">
        <IntakeRow label="First name" value={session.first_name} />
        <IntakeRow label="Email" value={session.email} mono />
        <IntakeRow label="Phone" value={session.phone || null} mono optional />
      </div>

      {sections.map(({ phase, questions }) => (
        <div key={phase}>
          <ResponseSectionHeader phase={phase} />
          <div className="px-6 pb-6 space-y-6">
            {/* Synthesize the qualifier + industry from session.qualifier_choice / session.industry
                rather than tempo_responses (those columns live on the session, not responses). */}
            {phase === 'qualifier' && (
              <QualifierResponse value={session.qualifier_choice} />
            )}
            {phase === 'context' && (
              <IndustryResponse value={session.industry} />
            )}
            {/* Path sections render from PRESENT responses only: the live intake
                was trimmed (Path A 17→9, Path B 17→8), but historical sessions
                still carry the full set in tempo_responses. Skipping absent
                answers keeps old records fully visible while new (trimmed)
                records render cleanly without phantom "(no answer)" rows. */}
            {(phase === 'pathA' || phase === 'pathB') && questions
              .filter(q => {
                const v = responses[q.id];
                return Array.isArray(v) ? v.length > 0 : v != null && v !== '';
              })
              .map(q => (
                <QuestionResponse key={q.id} q={q} value={responses[q.id]} />
              ))}
            {phase === 'convergence' && questions.map(q => (
              <QuestionResponse key={q.id} q={q} value={responses[q.id]} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ResponseSectionHeader({ phase }: { phase: Phase | 'intake' }) {
  const label = phase === 'intake' ? PHASE_LABEL.intake : PHASE_LABEL[phase];
  return (
    <div className="border-b border-rule px-6 py-3 bg-cream-dark/40">
      <h3 className="font-mono text-[11px] uppercase tracking-widest text-gold-warm">{label}</h3>
    </div>
  );
}

function IntakeRow({ label, value, mono = false, optional = false }: { label: string; value: string | null; mono?: boolean; optional?: boolean }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">{label}</div>
      {value ? (
        <div className={mono ? 'font-mono text-sm text-ink break-all' : 'font-serif text-base text-ink'}>{value}</div>
      ) : (
        <div className="font-serif text-sm italic text-ink-light">{optional ? '(not provided)' : '—'}</div>
      )}
    </div>
  );
}

function QualifierResponse({ value }: { value: string | null }) {
  const q = getQuestion('q1_qualifier');
  if (!q || q.kind !== 'radio') return null;
  const selected = value ? q.options.find(o => o.value === value) : null;
  return (
    <div>
      <p className="font-serif text-base text-ink leading-relaxed mb-3">{q.question}</p>
      {q.helper && <p className="font-serif text-xs italic text-ink-light mb-3">{q.helper}</p>}
      {selected ? (
        <div className="bg-cream-dark border-l-2 border-gold p-4">
          <div className="flex items-baseline gap-3 mb-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gold-warm">{value}</span>
            <span className="font-serif text-base font-medium text-ink">{selected.lead}</span>
          </div>
          {selected.desc && <p className="font-serif text-sm text-ink-soft leading-relaxed">{selected.desc}</p>}
        </div>
      ) : (
        <p className="font-serif text-sm italic text-ink-light">(no answer)</p>
      )}
    </div>
  );
}

function IndustryResponse({ value }: { value: string | null }) {
  const q = getQuestion('q2_industry');
  if (!q || q.kind !== 'radio') return null;
  const selected = value ? q.options.find(o => o.value === value) : null;
  return (
    <div>
      <p className="font-serif text-base text-ink leading-relaxed mb-3">{q.question}</p>
      {selected ? (
        <div className="bg-cream-dark border-l-2 border-gold p-4">
          <span className="font-serif text-base font-medium text-ink">{selected.emph || selected.value}</span>
          {selected.light && <span className="font-serif text-sm italic text-ink-light"> — {selected.light}</span>}
        </div>
      ) : (
        <p className="font-serif text-sm italic text-ink-light">(no answer)</p>
      )}
    </div>
  );
}

function QuestionResponse({ q, value }: { q: QuestionDef; value: any }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">{q.label}</div>
      <p className="font-serif text-base text-ink leading-relaxed mb-2">{q.question}</p>
      {q.helper && <p className="font-serif text-xs italic text-ink-light mb-3">{q.helper}</p>}
      <ResponseAnswer q={q} value={value} />
    </div>
  );
}

function ResponseAnswer({ q, value }: { q: QuestionDef; value: any }) {
  if (q.kind === 'radio') {
    if (!value) return <p className="font-serif text-sm italic text-ink-light">(no answer)</p>;
    const opt = getOption(q.id, String(value));
    return (
      <div className="bg-cream-dark border-l-2 border-gold p-4">
        <div className="flex items-baseline gap-3 mb-1">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gold-warm">{value}</span>
          {opt && 'lead' in opt && opt.lead && (
            <span className="font-serif text-base font-medium text-ink">{opt.lead}</span>
          )}
          {opt && 'emph' in opt && (opt as any).emph && (
            <span className="font-serif text-base font-medium text-ink">{(opt as any).emph}</span>
          )}
        </div>
        {opt && 'desc' in opt && opt.desc && (
          <p className="font-serif text-sm text-ink-soft leading-relaxed">{opt.desc}</p>
        )}
      </div>
    );
  }

  if (q.kind === 'checkbox') {
    const arr: string[] = Array.isArray(value) ? value : [];
    if (arr.length === 0) {
      return <p className="font-serif text-sm italic text-ink-light">(no answer)</p>;
    }
    if (arr.length === 1 && arr[0] === 'none') {
      return (
        <div className="bg-cream-dark border-l-2 border-rule p-4">
          <p className="font-serif text-sm italic text-ink-soft">Selected: none of the above</p>
        </div>
      );
    }
    return (
      <ul className="space-y-2">
        {arr.map((v, i) => {
          const opt = getOption(q.id, v) as ({ value: string; text: string } | null);
          return (
            <li key={i} className="bg-cream-dark border-l-2 border-gold p-3 flex items-baseline gap-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-gold-warm flex-shrink-0">✓</span>
              <span className="font-serif text-sm text-ink">{opt?.text || v}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  // textarea
  if (typeof value === 'string' && value.trim().length > 0) {
    return (
      <blockquote className="bg-paper border-l-4 border-gold p-4 font-serif text-base text-ink italic leading-relaxed whitespace-pre-wrap">
        {value}
      </blockquote>
    );
  }
  return (
    <p className="font-serif text-sm italic text-ink-light">{q.optional ? '(left blank)' : '(no answer)'}</p>
  );
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/50" onClick={onClose}>
      <div className="bg-paper border border-rule p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/**
 * Engagement sub-nav — shown at the top of the Review page when this
 * session is the customer discovery for an engagement. Mirrors the
 * portalShell sub-nav (workers/tempo/src/portal.ts) so the user has
 * the same four tabs from the synthesis review as from the engagement
 * detail / configurator pages. Engagement and Configure are
 * cross-domain back to /portal; View Customer Page is the public
 * /engagement/{slug}; View Synthesis is the active tab here.
 */
function EngagementSubnav({ engagement, sessionId: _sessionId }: { engagement: { slug: string; name: string }; sessionId: string }) {
  const baseLink: React.CSSProperties = {
    fontFamily: "'Newsreader', Georgia, serif",
    fontSize: 15,
    color: '#142849',
    textDecoration: 'none',
    paddingBottom: 4,
    borderBottom: '2px solid transparent',
  };
  const activeLink: React.CSSProperties = {
    ...baseLink,
    color: '#8a6914',
    fontWeight: 500,
    borderBottomColor: '#8a6914',
  };
  const breadcrumbStyle: React.CSSProperties = {
    fontFamily: "'JetBrains Mono', Menlo, monospace",
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: '#8a6914',
  };
  return (
    <>
      <div style={{ padding: '14px 32px', background: '#f5ecd7', borderBottom: '1px solid #d6cfb8' }}>
        <span style={breadcrumbStyle}>
          <a href={`https://cadenceadvisers.com/portal/engagements`} style={{ color: '#8a6914', textDecoration: 'none' }}>Engagements</a>
          <span style={{ color: 'rgba(74,85,104,0.6)', margin: '0 4px' }}>/</span>
          <a href={`https://cadenceadvisers.com/portal/engagements/${encodeURIComponent(engagement.slug)}`} style={{ color: '#8a6914', textDecoration: 'none' }}>{engagement.name}</a>
          <span style={{ color: 'rgba(74,85,104,0.6)', margin: '0 4px' }}>/</span>
          <span style={{ color: '#142849' }}>Synthesis</span>
        </span>
      </div>
      <nav style={{ padding: '12px 32px', background: '#fdfaf2', borderBottom: '1px solid #d6cfb8', display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }} aria-label="Engagement sections">
        <a href={`https://cadenceadvisers.com/portal/engagements/${encodeURIComponent(engagement.slug)}`} style={baseLink}>Engagement</a>
        <a href={`https://cadenceadvisers.com/portal/engagements/${encodeURIComponent(engagement.slug)}/configure`} style={baseLink}>Configure</a>
        <a style={activeLink} aria-current="page">View Synthesis</a>
        <a href={`https://cadenceadvisers.com/engagement/${encodeURIComponent(engagement.slug)}`} style={baseLink}>View Customer Page</a>
      </nav>
    </>
  );
}

// ─── Versions sidebar (Task 035) ───────────────────────────────────

function VersionsSidebar({
  runs,
  selectedRunId,
  onSelect,
  onPromote,
  promotePromptId,
  onPromoteCancel,
  onPromoteConfirm,
  onGenerateClick,
  onBlendClick,
  blendSelection,
  onToggleBlend,
}: {
  runs: SynthesisRun[];
  selectedRunId: string | null;
  onSelect: (id: string) => void;
  onPromote: (id: string) => void;
  promotePromptId: string | null;
  onPromoteCancel: () => void;
  onPromoteConfirm: (id: string) => void;
  onGenerateClick: () => void;
  onBlendClick: () => void;
  blendSelection: Set<string>;
  onToggleBlend: (id: string) => void;
}) {
  // Run #N indexes by created_at ASC (the canonical session ordering).
  // Used for the "Sources: #X, #Y" line on blend rows.
  const runNumberById = new Map<string, number>();
  const byCreated = [...runs].sort((a, b) => a.created_at.localeCompare(b.created_at));
  byCreated.forEach((r, i) => runNumberById.set(r.id, i + 1));
  // Blend #N indexes by created_at ASC across blends only
  const blendNumberById = new Map<string, number>();
  byCreated.filter(r => r.run_type === 'blend').forEach((r, i) => blendNumberById.set(r.id, i + 1));
  const labelFor = (run: SynthesisRun): string => {
    if (run.run_type === 'blend') return `Blend #${blendNumberById.get(run.id) ?? '?'}`;
    return runLabel(run, 0);
  };
  const blendDisabled = blendSelection.size !== 2;
  return (
    <div className="space-y-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-gold-warm">
        Versions {runs.length > 0 && <span className="text-ink-light">({runs.length})</span>}
      </div>
      {runs.length === 0 && (
        <div className="font-serif text-sm italic text-ink-light bg-paper border border-rule p-3">
          No runs yet. Original synthesis will appear once generated.
        </div>
      )}
      {runs.map((run) => {
        const isSelected = run.id === selectedRunId;
        const isCheckable = run.status === 'completed';
        const isChecked = blendSelection.has(run.id);
        const showPromotePrompt = promotePromptId === run.id;
        const labelText = labelFor(run);
        return (
          <div
            key={run.id}
            className={`bg-paper border ${isSelected ? 'border-gold' : 'border-rule'} p-3 text-sm relative`}
          >
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={isChecked}
                disabled={!isCheckable}
                onChange={() => onToggleBlend(run.id)}
                className="mt-1 accent-gold disabled:opacity-30"
                title={isCheckable ? 'Select for blend (B3 enables Blend button)' : 'Run not completed'}
              />
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => onSelect(run.id)}
                  disabled={run.status !== 'completed'}
                  className={`font-serif text-base text-left w-full truncate ${run.status === 'completed' ? 'text-ink hover:text-accent' : 'text-ink-light cursor-default'}`}
                  title={labelText}
                >
                  {labelText}
                </button>
                <div className="font-mono text-[9px] uppercase tracking-widest text-gold-warm mt-1">
                  {run.run_type === 'blend'
                    ? `[${shortModel(run.pass_2_model)}·BLEND]`
                    : `[${shortModel(run.pass_1_model)}·${shortModel(run.pass_2_model)}]`}
                </div>
                {run.run_type === 'blend' && run.parent_run_ids && run.parent_run_ids.length > 0 && (
                  <div className="font-mono text-[10px] text-gold-warm mt-1">
                    Sources: {run.parent_run_ids.map(pid => `#${runNumberById.get(pid) ?? '?'}`).join(', ')}
                  </div>
                )}
                <div className="font-mono text-[10px] text-ink-light mt-1">
                  {formatCost(run.total_cost_usd)} · {formatRunTime(run.created_at)}
                </div>
                {run.is_primary && run.status === 'completed' && (
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gold-warm mt-1">★ PRIMARY</div>
                )}
                {run.status === 'running' && (
                  <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light mt-1 italic">running…</div>
                )}
                {run.status === 'failed' && (
                  <div className="font-mono text-[10px] text-accent mt-1 italic" title={run.error_message || ''}>
                    failed: <span className="break-all">{(run.error_message || '').slice(0, 60)}{(run.error_message || '').length > 60 ? '…' : ''}</span>
                  </div>
                )}
                {!run.is_primary && run.status === 'completed' && !showPromotePrompt && (
                  <button
                    onClick={() => onPromote(run.id)}
                    className="mt-2 font-mono text-[10px] uppercase tracking-widest text-ink-light hover:text-accent"
                  >
                    Make primary →
                  </button>
                )}
                {showPromotePrompt && (
                  <div className="mt-2 bg-cream-dark/50 border border-rule p-2">
                    <div className="font-serif text-xs text-ink mb-2">
                      Mark "{labelText}" as primary? The current primary becomes an alternative.
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={onPromoteCancel}
                        className="font-mono text-[10px] uppercase tracking-widest text-ink-light hover:text-ink"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => onPromoteConfirm(run.id)}
                        className="font-mono text-[10px] uppercase tracking-widest text-accent hover:underline"
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <button
        onClick={onGenerateClick}
        className="w-full bg-paper border border-rule p-3 font-mono text-[11px] uppercase tracking-wider text-ink-soft hover:text-accent hover:border-accent transition-colors text-center"
      >
        + Generate alternative
      </button>
      <button
        onClick={onBlendClick}
        disabled={blendDisabled}
        title={blendDisabled ? 'Select exactly 2 versions to blend' : `Blend ${blendSelection.size} selected versions`}
        className="w-full bg-paper border border-rule p-3 font-mono text-[11px] uppercase tracking-wider text-ink-soft hover:text-accent hover:border-accent disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-ink-soft disabled:hover:border-rule text-center transition-colors"
      >
        + Blend selected
      </button>
    </div>
  );
}

// ─── Generate alternative modal (Task 035) ─────────────────────────

function GenerateAlternativeModal({
  state,
  onCancel,
  onSubmit,
}: {
  state: GenerateState;
  onCancel: () => void;
  onSubmit: (args: { preset: Preset; pass1?: { provider: string; model: string }; pass2?: { provider: string; model: string } }) => Promise<void>;
}) {
  const [preset, setPreset] = useState<Preset>('anthropic_standard');
  const [p1Provider, setP1Provider] = useState('anthropic');
  const [p1Model, setP1Model] = useState('claude-sonnet-4-6');
  const [p2Provider, setP2Provider] = useState('anthropic');
  const [p2Model, setP2Model] = useState('claude-opus-4-7');

  const submitting = state.phase === 'submitting';
  const errorState = state.phase === 'error' ? state : null;

  function modelsForProvider(provider: string): string[] {
    if (provider === 'anthropic') return ['claude-sonnet-4-6', 'claude-opus-4-7', 'claude-haiku-4-5'];
    if (provider === 'openai') return ['gpt-5', 'gpt-5-pro'];
    if (provider === 'google') return ['gemini-2.5-flash', 'gemini-2.5-pro'];
    return [];
  }

  function handleGo() {
    if (preset === 'custom') {
      onSubmit({
        preset,
        pass1: { provider: p1Provider, model: p1Model },
        pass2: { provider: p2Provider, model: p2Model },
      });
    } else {
      onSubmit({ preset });
    }
  }

  const customIncomplete = preset === 'custom' && (!p1Provider || !p1Model || !p2Provider || !p2Model);

  return (
    <Modal onClose={submitting ? () => undefined : onCancel}>
      <h2 className="font-display text-2xl font-medium text-ink mb-3">Generate alternative synthesis</h2>
      <div className="text-sm font-serif text-ink-soft mb-4">
        Choose a preset. Generation is synchronous and takes ~60–90s.
      </div>
      <div className="space-y-2 mb-5">
        {(Object.keys(PRESET_LABELS) as Preset[]).map(key => {
          const est = key !== 'custom' ? PRESET_ESTIMATES[key as Exclude<Preset, 'custom'>] : null;
          return (
            <label key={key} className="flex items-start gap-2 text-sm font-serif text-ink cursor-pointer">
              <input
                type="radio"
                name="preset"
                value={key}
                checked={preset === key}
                onChange={() => setPreset(key)}
                disabled={submitting}
                className="mt-1 accent-gold"
              />
              <span className="flex-1">
                {PRESET_LABELS[key]}
                {est && <span className="text-ink-light ml-2 font-mono text-xs">{est.cost} · {est.duration}</span>}
              </span>
            </label>
          );
        })}
      </div>
      {preset === 'custom' && (
        <div className="bg-cream-dark/40 border border-rule p-3 mb-5 space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light">Pass 1 (analysis)</div>
          <div className="flex gap-2">
            <select value={p1Provider} onChange={e => { setP1Provider(e.target.value); setP1Model(modelsForProvider(e.target.value)[0] || ''); }} disabled={submitting} className="flex-1 p-2 bg-paper border border-rule font-serif text-sm">
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
              <option value="google">Google</option>
            </select>
            <select value={p1Model} onChange={e => setP1Model(e.target.value)} disabled={submitting} className="flex-1 p-2 bg-paper border border-rule font-mono text-xs">
              {modelsForProvider(p1Provider).map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light">Pass 2 (writing)</div>
          <div className="flex gap-2">
            <select value={p2Provider} onChange={e => { setP2Provider(e.target.value); setP2Model(modelsForProvider(e.target.value)[0] || ''); }} disabled={submitting} className="flex-1 p-2 bg-paper border border-rule font-serif text-sm">
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
              <option value="google">Google</option>
            </select>
            <select value={p2Model} onChange={e => setP2Model(e.target.value)} disabled={submitting} className="flex-1 p-2 bg-paper border border-rule font-mono text-xs">
              {modelsForProvider(p2Provider).map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
      )}
      {submitting && (
        <div className="bg-cream-dark/40 border border-rule p-3 mb-5 font-serif text-sm italic text-ink-soft">
          Running Pass 1 then Pass 2 — this stays synchronous and takes about 60–90 seconds. Don't close this modal.
        </div>
      )}
      {errorState && (
        <div className="bg-paper border border-accent p-3 mb-5 font-serif text-sm text-ink">
          <strong className="text-accent">Generation failed.</strong>
          <div className="text-ink-soft mt-1 text-xs font-mono break-all">{errorState.message}</div>
        </div>
      )}
      <div className="flex justify-end gap-3">
        <button onClick={onCancel} disabled={submitting} className="px-4 py-2 font-serif text-sm text-ink-light hover:text-ink disabled:opacity-50">
          {errorState ? 'Close' : 'Cancel'}
        </button>
        <button
          onClick={handleGo}
          disabled={submitting || customIncomplete}
          className="px-5 py-2 bg-ink text-paper font-serif text-sm hover:bg-accent disabled:opacity-50"
        >
          {submitting ? 'Generating…' : errorState ? 'Try again' : 'Generate'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Blend modal (Task 036) ───────────────────────────────────────

function BlendModal({
  state,
  runs,
  onCancel,
  onSubmit,
}: {
  state: BlendState;
  runs: SynthesisRun[];
  onCancel: () => void;
  onSubmit: (args: { parentIds: [string, string]; preset: Preset; blendInstructions: string; pass2?: { provider: string; model: string } }) => Promise<void>;
}) {
  const [preset, setPreset] = useState<Preset>('anthropic_standard');
  const [p2Provider, setP2Provider] = useState('anthropic');
  const [p2Model, setP2Model] = useState('claude-opus-4-7');
  const [instructionMode, setInstructionMode] = useState<'default' | 'custom'>('default');
  const [customInstructions, setCustomInstructions] = useState('');

  if (state.phase === 'idle') return null;
  const submitting = state.phase === 'submitting';
  const errorState = state.phase === 'error' ? state : null;
  const parentIds: [string, string] = state.phase === 'open'
    ? state.parentIds
    : (state as any).parentIds;

  const parents = parentIds.map(id => runs.find(r => r.id === id)).filter(Boolean) as SynthesisRun[];

  function modelsForProvider(provider: string): string[] {
    if (provider === 'anthropic') return ['claude-opus-4-7', 'claude-sonnet-4-6', 'claude-haiku-4-5'];
    if (provider === 'openai') return ['gpt-5-pro', 'gpt-5'];
    if (provider === 'google') return ['gemini-2.5-pro', 'gemini-2.5-flash'];
    return [];
  }

  // Blend cost estimates — roughly half an alternative since only Pass 2.
  const BLEND_ESTIMATES: Record<Exclude<Preset, 'custom'>, string> = {
    anthropic_standard: '~$0.20',
    anthropic_fast: '~$0.05',
    openai_standard: '~$0.30',
    google_standard: '~$0.03',
  };

  const customIncomplete = preset === 'custom' && (!p2Provider || !p2Model);
  const instructions = instructionMode === 'default' ? DEFAULT_BLEND_INSTRUCTIONS : customInstructions.trim();
  const instructionsEmpty = !instructions;

  function handleGo() {
    onSubmit({
      parentIds,
      preset,
      blendInstructions: instructions,
      pass2: preset === 'custom' ? { provider: p2Provider, model: p2Model } : undefined,
    });
  }

  return (
    <Modal onClose={submitting ? () => undefined : onCancel}>
      <h2 className="font-display text-2xl font-medium text-ink mb-3">Blend selected versions</h2>
      <div className="text-sm font-serif text-ink-soft mb-2">You're blending:</div>
      <ul className="text-sm font-serif text-ink mb-5 space-y-1 list-disc pl-5">
        {parents.map(p => (
          <li key={p.id}>
            {runLabel(p, 0)} <span className="text-ink-light font-mono text-xs">({shortModel(p.pass_1_model)}/{shortModel(p.pass_2_model)})</span>
            <span className="font-mono text-xs text-gold-warm ml-2">{formatCost(p.total_cost_usd)}</span>
          </li>
        ))}
      </ul>

      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-2">Instructions</div>
        <label className="flex items-start gap-2 text-sm font-serif text-ink mb-2 cursor-pointer">
          <input type="radio" name="bi" checked={instructionMode === 'default'} onChange={() => setInstructionMode('default')} disabled={submitting} className="mt-1 accent-gold" />
          <span className="flex-1 italic">{DEFAULT_BLEND_INSTRUCTIONS}</span>
        </label>
        <label className="flex items-start gap-2 text-sm font-serif text-ink cursor-pointer">
          <input type="radio" name="bi" checked={instructionMode === 'custom'} onChange={() => setInstructionMode('custom')} disabled={submitting} className="mt-1 accent-gold" />
          <span className="flex-1">Custom</span>
        </label>
        {instructionMode === 'custom' && (
          <textarea
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            disabled={submitting}
            rows={4}
            placeholder="Describe how to blend these — what to keep, what to drop, what to emphasize."
            className="w-full mt-2 p-2 bg-paper border border-rule font-serif text-sm focus:outline-none focus:border-accent"
          />
        )}
      </div>

      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-2">Blend model (Pass 2 only — no analysis step)</div>
        <div className="space-y-2">
          {(Object.keys(PRESET_LABELS) as Preset[]).map(key => (
            <label key={key} className="flex items-start gap-2 text-sm font-serif text-ink cursor-pointer">
              <input
                type="radio"
                name="blend-preset"
                value={key}
                checked={preset === key}
                onChange={() => setPreset(key)}
                disabled={submitting}
                className="mt-1 accent-gold"
              />
              <span className="flex-1">
                {PRESET_LABELS[key]}
                {key !== 'custom' && <span className="text-ink-light ml-2 font-mono text-xs">{BLEND_ESTIMATES[key as Exclude<Preset, 'custom'>]}</span>}
              </span>
            </label>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="bg-cream-dark/40 border border-rule p-3 mt-3 space-y-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light">Pass 2 (writing)</div>
            <div className="flex gap-2">
              <select value={p2Provider} onChange={e => { setP2Provider(e.target.value); setP2Model(modelsForProvider(e.target.value)[0] || ''); }} disabled={submitting} className="flex-1 p-2 bg-paper border border-rule font-serif text-sm">
                <option value="anthropic">Anthropic</option>
                <option value="openai">OpenAI</option>
                <option value="google">Google</option>
              </select>
              <select value={p2Model} onChange={e => setP2Model(e.target.value)} disabled={submitting} className="flex-1 p-2 bg-paper border border-rule font-mono text-xs">
                {modelsForProvider(p2Provider).map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {submitting && (
        <div className="bg-cream-dark/40 border border-rule p-3 mb-5 font-serif text-sm italic text-ink-soft">
          Critiquing both versions and writing a blended synthesis — synchronous, ~60–90 seconds.
        </div>
      )}
      {errorState && (
        <div className="bg-paper border border-accent p-3 mb-5 font-serif text-sm text-ink">
          <strong className="text-accent">Blend failed.</strong>
          <div className="text-ink-soft mt-1 text-xs font-mono break-all">{errorState.message}</div>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button onClick={onCancel} disabled={submitting} className="px-4 py-2 font-serif text-sm text-ink-light hover:text-ink disabled:opacity-50">
          {errorState ? 'Close' : 'Cancel'}
        </button>
        <button
          onClick={handleGo}
          disabled={submitting || customIncomplete || instructionsEmpty}
          className="px-5 py-2 bg-ink text-paper font-serif text-sm hover:bg-accent disabled:opacity-50"
        >
          {submitting ? 'Blending…' : errorState ? 'Try again' : 'Blend'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Compare synthesis (section-aligned side-by-side, Task 035) ────

function splitSections(text: string | null | undefined): { heading: string; body: string }[] {
  if (!text) return [];
  // Section headers in the synthesis are H1/H2 markdown; split on lines
  // beginning with one or two #. Pre-section content is stored under
  // an empty heading so it still renders.
  const lines = text.split(/\r?\n/);
  const sections: { heading: string; body: string[] }[] = [{ heading: '', body: [] }];
  for (const line of lines) {
    const m = line.match(/^(#{1,2})\s+(.*)$/);
    if (m) {
      sections.push({ heading: m[2].trim(), body: [] });
    } else {
      sections[sections.length - 1].body.push(line);
    }
  }
  return sections
    .filter((s, i) => i === 0 ? s.body.some(l => l.trim()) : true)
    .map(s => ({ heading: s.heading, body: s.body.join('\n').trim() }));
}

function CompareSynthesis({
  left,
  right,
  allRuns,
  onSelectPeer,
}: {
  left: SynthesisRun | null;
  right: SynthesisRun | null;
  allRuns: SynthesisRun[];
  onSelectPeer: (id: string) => void;
}) {
  const peerCandidates = allRuns.filter(r => r.id !== left?.id && r.status === 'completed');
  const leftSections = splitSections(left?.synthesis_text);
  const rightSections = splitSections(right?.synthesis_text);
  // Best-effort align by heading text; fall back to index-based pairing.
  const headings: string[] = [];
  const seen = new Set<string>();
  for (const s of leftSections) {
    if (!seen.has(s.heading)) { headings.push(s.heading); seen.add(s.heading); }
  }
  for (const s of rightSections) {
    if (!seen.has(s.heading)) { headings.push(s.heading); seen.add(s.heading); }
  }
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-2">
          {left ? runLabel(left, 1) : 'No version'}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light">Compare against:</span>
          <select
            value={right?.id || ''}
            onChange={(e) => onSelectPeer(e.target.value)}
            className="p-1 bg-paper border border-rule font-serif text-xs"
          >
            {peerCandidates.length === 0 && <option value="">(no other completed versions)</option>}
            {peerCandidates.map(r => (
              <option key={r.id} value={r.id}>{runLabel(r, 1)}</option>
            ))}
          </select>
        </div>
      </div>
      {headings.map(h => {
        const l = leftSections.find(s => s.heading === h);
        const r = rightSections.find(s => s.heading === h);
        return (
          <div key={h} className="contents">
            <div className="bg-paper border border-rule p-4">
              {h && <div className="font-display text-lg font-medium text-ink mb-2">{h}</div>}
              <div className="font-serif text-sm text-ink whitespace-pre-wrap leading-relaxed">{l?.body || <span className="italic text-ink-light">(absent)</span>}</div>
            </div>
            <div className="bg-paper border border-rule p-4">
              {h && <div className="font-display text-lg font-medium text-ink mb-2">{h}</div>}
              <div className="font-serif text-sm text-ink whitespace-pre-wrap leading-relaxed">{r?.body || <span className="italic text-ink-light">(absent)</span>}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Compare analysis (Internal Analysis side-by-side, Task 036) ───

function CompareAnalysis({
  left,
  right,
  allRuns,
  onSelectPeer,
}: {
  left: SynthesisRun | null;
  right: SynthesisRun | null;
  allRuns: SynthesisRun[];
  onSelectPeer: (id: string) => void;
}) {
  const peerCandidates = allRuns.filter(r => r.id !== left?.id && r.status === 'completed');
  const la = left?.synthesis_analysis || null;
  const ra = right?.synthesis_analysis || null;

  // Blends carry no Pass 1 analysis (Pass 2-only operation). Surface that
  // explicitly so the right column doesn't look like a parsing failure.
  const leftMissing = !!left && left.run_type === 'blend';
  const rightMissing = !!right && right.run_type === 'blend';

  function renderListSide(value: any): React.ReactNode {
    if (value == null) return <span className="italic text-ink-light">(none)</span>;
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="italic text-ink-light">(empty)</span>;
      return (
        <ul className="space-y-2 list-disc pl-5">
          {value.map((item, i) => (
            <li key={i} className="font-serif text-sm text-ink">
              {typeof item === 'string'
                ? item
                : <pre className="whitespace-pre-wrap font-mono text-xs">{JSON.stringify(item, null, 2)}</pre>}
            </li>
          ))}
        </ul>
      );
    }
    if (typeof value === 'object') {
      return <pre className="whitespace-pre-wrap font-mono text-xs text-ink">{JSON.stringify(value, null, 2)}</pre>;
    }
    return <span className="font-serif text-sm text-ink">{String(value)}</span>;
  }

  function Row({ heading, leftValue, rightValue }: { heading: string; leftValue: any; rightValue: any }) {
    return (
      <div className="contents">
        <div className="bg-paper border border-rule p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gold-warm mb-2">{heading}</div>
          {renderListSide(leftValue)}
        </div>
        <div className="bg-paper border border-rule p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gold-warm mb-2">{heading}</div>
          {renderListSide(rightValue)}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2 grid grid-cols-2 gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-2">
            {left ? runLabel(left, 1) : 'No version'}
            {leftMissing && <span className="ml-2 italic text-ink-light">(no analysis — blend)</span>}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light">Compare against:</span>
            <select
              value={right?.id || ''}
              onChange={(e) => onSelectPeer(e.target.value)}
              className="p-1 bg-paper border border-rule font-serif text-xs"
            >
              {peerCandidates.length === 0 && <option value="">(no other completed versions)</option>}
              {peerCandidates.map(r => (
                <option key={r.id} value={r.id}>{runLabel(r, 1)}</option>
              ))}
            </select>
            {rightMissing && <span className="ml-1 italic font-mono text-[10px] text-ink-light">(no analysis — blend)</span>}
          </div>
        </div>
      </div>
      {/* Score & register */}
      <div className="col-span-2 grid grid-cols-2 gap-4">
        <div className="bg-paper border border-rule p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gold-warm mb-2">Score & register</div>
          {la ? (
            <>
              <div className="flex items-baseline gap-3 mb-2">
                <div className="font-display text-3xl font-medium text-ink">{la.maturity_score ?? '—'}</div>
                <div className="font-mono text-xs text-ink-light">/ 10</div>
                <div className="font-serif text-sm text-gold-warm">{la.register || '—'}</div>
              </div>
              {la.register_rationale && <p className="font-serif text-xs italic text-ink-soft">{la.register_rationale}</p>}
            </>
          ) : <span className="italic text-ink-light">(none)</span>}
        </div>
        <div className="bg-paper border border-rule p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gold-warm mb-2">Score & register</div>
          {ra ? (
            <>
              <div className="flex items-baseline gap-3 mb-2">
                <div className="font-display text-3xl font-medium text-ink">{ra.maturity_score ?? '—'}</div>
                <div className="font-mono text-xs text-ink-light">/ 10</div>
                <div className="font-serif text-sm text-gold-warm">{ra.register || '—'}</div>
              </div>
              {ra.register_rationale && <p className="font-serif text-xs italic text-ink-soft">{ra.register_rationale}</p>}
            </>
          ) : <span className="italic text-ink-light">(none)</span>}
        </div>
      </div>
      <Row heading="Patterns" leftValue={la?.dominant_patterns} rightValue={ra?.dominant_patterns} />
      <Row heading="Ranked priorities" leftValue={la?.ranked_priorities} rightValue={ra?.ranked_priorities} />
      <Row heading="Top friction points" leftValue={la?.top_friction_points} rightValue={ra?.top_friction_points} />
      <Row heading="Recommendations to avoid" leftValue={la?.anti_recommendations} rightValue={ra?.anti_recommendations} />
      <Row heading="Next step" leftValue={la?.next_step} rightValue={ra?.next_step} />
      <Row heading="Tone flags" leftValue={la?.tone_flags} rightValue={ra?.tone_flags} />
      <Row heading="Industry-specific notes" leftValue={la?.industry_specific_notes} rightValue={ra?.industry_specific_notes} />
    </div>
  );
}

function qualifierLabel(c: string | null): string {
  const map: Record<string, string> = {
    A: 'Lean & founder-driven',
    B: 'Departmentalized & management-led',
    C: 'Hybrid: bench gap',
    D: 'Lean but self-managed',
  };
  return c ? (map[c] || c) : '—';
}

function industryShort(key: string | null): string {
  if (!key) return '—';
  const map: Record<string, string> = {
    professional_services: 'Professional services',
    real_estate: 'Real estate',
    health_wellness: 'Health & wellness',
    ecommerce_retail: 'E-commerce',
    trades_manufacturing: 'Trades / mfg',
    saas_software: 'SaaS',
    other: 'Other',
  };
  return map[key] || key;
}
